import { WaveformConfig, VideoPlayerConfig } from '@/types/media'

/**
 * Cosmic theme configuration for Wavesurfer.js
 */
export const cosmicWaveformConfig: WaveformConfig = {
  waveColor: '#64ffda', // stellar-cyan with glow
  progressColor: '#00d4ff', // stellar-blue with glow  
  cursorColor: '#ff6ec7', // stellar-pink with glow
  barWidth: undefined, // Continuous waveform like DAW (not bars)
  barGap: undefined, // Not needed for continuous waveform
  barRadius: undefined, // Not needed for continuous waveform  
  height: 80,
  normalize: true,
  fillParent: true,
  responsive: true,
  cursorWidth: 2, // Visible playhead cursor
  interact: true, // Allow clicking to seek
  dragToSeek: true // Allow dragging to seek like DAW
}

/**
 * Cosmic theme configuration for Video.js
 */
export const cosmicVideoConfig: VideoPlayerConfig = {
  controls: true,
  fluid: true,
  responsive: true,
  preload: 'metadata',
  playbackRates: [0.5, 1, 1.25, 1.5, 2]
}

/**
 * CSS styles for cosmic-themed Video.js player
 * These styles integrate with the existing cosmic design system
 */
export const cosmicVideoStyles = `
  .video-js.vjs-cosmic {
    background-color: var(--cosmic-dark, #1a1a2e);
    color: var(--stellar-cyan, #64ffda);
    font-family: 'Inter', sans-serif;
  }

  .video-js.vjs-cosmic .vjs-control-bar {
    background: linear-gradient(135deg, var(--cosmic-dark, #1a1a2e) 0%, var(--nebula-deep, #16213e) 100%);
    border-top: 1px solid var(--stellar-cyan, #64ffda);
    backdrop-filter: blur(10px);
  }

  .video-js.vjs-cosmic .vjs-play-control {
    color: var(--stellar-blue, #00d4ff);
  }

  .video-js.vjs-cosmic .vjs-play-control:hover {
    color: var(--stellar-cyan, #64ffda);
    text-shadow: 0 0 10px var(--stellar-cyan, #64ffda);
  }

  .video-js.vjs-cosmic .vjs-progress-control {
    height: 6px;
  }

  .video-js.vjs-cosmic .vjs-progress-holder {
    background-color: var(--stellar-cyan, #64ffda);
    opacity: 0.3;
    border-radius: 3px;
  }

  .video-js.vjs-cosmic .vjs-play-progress {
    background: linear-gradient(90deg, var(--stellar-blue, #00d4ff), var(--stellar-cyan, #64ffda));
    border-radius: 3px;
    box-shadow: 0 0 10px var(--stellar-blue, #00d4ff);
  }

  .video-js.vjs-cosmic .vjs-play-progress:before {
    color: var(--stellar-pink, #ff6ec7);
    text-shadow: 0 0 5px var(--stellar-pink, #ff6ec7);
  }

  .video-js.vjs-cosmic .vjs-volume-control {
    color: var(--stellar-cyan, #64ffda);
  }

  .video-js.vjs-cosmic .vjs-volume-bar {
    background-color: var(--stellar-cyan, #64ffda);
    opacity: 0.3;
  }

  .video-js.vjs-cosmic .vjs-volume-level {
    background: linear-gradient(90deg, var(--stellar-blue, #00d4ff), var(--stellar-cyan, #64ffda));
  }

  .video-js.vjs-cosmic .vjs-fullscreen-control {
    color: var(--stellar-cyan, #64ffda);
  }

  .video-js.vjs-cosmic .vjs-fullscreen-control:hover {
    color: var(--stellar-pink, #ff6ec7);
    text-shadow: 0 0 5px var(--stellar-pink, #ff6ec7);
  }

  .video-js.vjs-cosmic .vjs-time-control {
    color: var(--stellar-cyan, #64ffda);
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.875rem;
  }

  .video-js.vjs-cosmic .vjs-remaining-time {
    color: var(--stellar-cyan, #64ffda);
    opacity: 0.8;
  }

  .video-js.vjs-cosmic .vjs-big-play-button {
    background: linear-gradient(135deg, var(--stellar-blue, #00d4ff), var(--stellar-cyan, #64ffda));
    border: 2px solid var(--stellar-cyan, #64ffda);
    border-radius: 50%;
    color: var(--cosmic-black, #0a0a0f);
    font-size: 2rem;
    height: 80px;
    width: 80px;
    line-height: 76px;
    left: 50%;
    top: 50%;
    margin-left: -40px;
    margin-top: -40px;
    transition: all 0.3s ease;
  }

  .video-js.vjs-cosmic .vjs-big-play-button:hover {
    background: linear-gradient(135deg, var(--stellar-cyan, #64ffda), var(--stellar-pink, #ff6ec7));
    box-shadow: 0 0 20px var(--stellar-blue, #00d4ff);
    transform: scale(1.1);
  }

  .video-js.vjs-cosmic .vjs-loading-spinner {
    border-color: var(--stellar-cyan, #64ffda) transparent transparent transparent;
  }

  .video-js.vjs-cosmic .vjs-poster {
    background-color: var(--cosmic-dark, #1a1a2e);
  }

  .video-js.vjs-cosmic .vjs-menu-content {
    background-color: var(--cosmic-dark, #1a1a2e);
    border: 1px solid var(--stellar-cyan, #64ffda);
    border-radius: 8px;
    backdrop-filter: blur(10px);
  }

  .video-js.vjs-cosmic .vjs-menu li {
    color: var(--stellar-cyan, #64ffda);
    padding: 8px 12px;
  }

  .video-js.vjs-cosmic .vjs-menu li:hover {
    background-color: var(--stellar-cyan, #64ffda);
    color: var(--cosmic-black, #0a0a0f);
  }

  .video-js.vjs-cosmic .vjs-menu li.vjs-selected {
    background-color: var(--stellar-blue, #00d4ff);
    color: var(--cosmic-black, #0a0a0f);
  }

  /* Picture-in-Picture button styling */
  .video-js.vjs-cosmic .vjs-picture-in-picture-control {
    color: var(--stellar-cyan, #64ffda);
  }

  .video-js.vjs-cosmic .vjs-picture-in-picture-control:hover {
    color: var(--stellar-gold, #ffd700);
    text-shadow: 0 0 5px var(--stellar-gold, #ffd700);
  }

  /* Error display styling */
  .video-js.vjs-cosmic .vjs-error-display {
    background: linear-gradient(135deg, var(--cosmic-dark, #1a1a2e), var(--danger-red, #ff3366));
    color: white;
    border: 1px solid var(--danger-red, #ff3366);
    border-radius: 8px;
  }

  .video-js.vjs-cosmic .vjs-modal-dialog .vjs-modal-dialog-content {
    background-color: var(--cosmic-dark, #1a1a2e);
    color: var(--stellar-cyan, #64ffda);
    border: 1px solid var(--stellar-cyan, #64ffda);
    border-radius: 8px;
    backdrop-filter: blur(10px);
  }

  /* Cosmic Waveform Glow Effects */
  .cosmic-waveform-container {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
  }

  /* Simplified border glow - no pseudo elements that might interfere */
  .cosmic-waveform-container {
    box-shadow: 
      0 0 8px rgba(100, 255, 218, 0.3),
      0 0 16px rgba(0, 212, 255, 0.2);
  }


  /* Waveform canvas glow effects - simplified to avoid conflicts */
  .cosmic-waveform-container canvas {
    filter: drop-shadow(0 0 6px rgba(100, 255, 218, 0.4));
    transition: filter 0.3s ease;
  }

  .cosmic-waveform-container:hover canvas {
    filter: drop-shadow(0 0 10px rgba(100, 255, 218, 0.6));
  }

  /* Pulsing effect for playing state */
  .cosmic-waveform-playing {
    animation: cosmicWaveformPulse 1.5s ease-in-out infinite;
  }

  @keyframes cosmicWaveformPulse {
    0%, 100% {
      box-shadow: 
        0 0 10px rgba(100, 255, 218, 0.4),
        0 0 20px rgba(0, 212, 255, 0.3),
        0 0 30px rgba(255, 110, 199, 0.2);
    }
    50% {
      box-shadow: 
        0 0 15px rgba(100, 255, 218, 0.6),
        0 0 30px rgba(0, 212, 255, 0.5),
        0 0 45px rgba(255, 110, 199, 0.4);
    }
  }

`

/**
 * Get cosmic-themed Wavesurfer configuration based on container size
 */
export function getCosmicWaveformConfig(height?: number): WaveformConfig {
  // Get computed colors from CSS custom properties with fallbacks
  const rootStyles = getComputedStyle(document.documentElement)
  const stellarCyan = rootStyles.getPropertyValue('--stellar-cyan').trim() || '#64ffda'
  const stellarBlue = rootStyles.getPropertyValue('--stellar-blue').trim() || '#00d4ff'
  const stellarPink = rootStyles.getPropertyValue('--stellar-pink').trim() || '#ff6ec7'

  return {
    ...cosmicWaveformConfig,
    waveColor: stellarCyan,
    progressColor: stellarBlue,
    cursorColor: stellarPink,
    height: height || cosmicWaveformConfig.height
  }
}

/**
 * Get cosmic-themed Video.js configuration with custom options
 */
export function getCosmicVideoConfig(customConfig?: Partial<VideoPlayerConfig>): VideoPlayerConfig {
  return {
    ...cosmicVideoConfig,
    ...customConfig
  }
}

/**
 * Apply cosmic theme styles to a Video.js player
 */
export function applyCosmicTheme(player: any): void {
  if (player && player.el_) {
    player.el_.classList.add('vjs-cosmic')
    
    // Inject styles if not already present
    if (!document.getElementById('cosmic-video-styles')) {
      const styleElement = document.createElement('style')
      styleElement.id = 'cosmic-video-styles'
      styleElement.textContent = cosmicVideoStyles
      document.head.appendChild(styleElement)
    }
  }
}

/**
 * Remove cosmic theme from a Video.js player
 */
export function removeCosmicTheme(player: any): void {
  if (player && player.el_) {
    player.el_.classList.remove('vjs-cosmic')
  }
}

/**
 * Apply cosmic glow effects to a waveform container
 */
export function applyCosmicWaveformGlow(container: HTMLElement, isPlaying: boolean = false): void {
  if (!container) return

  // Inject styles if not already present
  if (!document.getElementById('cosmic-waveform-styles')) {
    const styleElement = document.createElement('style')
    styleElement.id = 'cosmic-waveform-styles'
    styleElement.textContent = cosmicVideoStyles // This includes the waveform styles
    document.head.appendChild(styleElement)
  }

  // Add cosmic waveform classes
  container.classList.add('cosmic-waveform-container')
  
  if (isPlaying) {
    container.classList.add('cosmic-waveform-playing')
  } else {
    container.classList.remove('cosmic-waveform-playing')
  }
}

/**
 * Remove cosmic glow effects from a waveform container
 */
export function removeCosmicWaveformGlow(container: HTMLElement): void {
  if (!container) return
  
  container.classList.remove('cosmic-waveform-container', 'cosmic-waveform-playing')
}